import { useState, useCallback } from 'react';
import Papa from 'papaparse';
import type { BatchImportRow } from '@/types';
import { api } from '@/lib/api';

const REQUIRED_COLUMNS = ['name', 'price', 'category', 'sku', 'stock', 'description'];

interface UseBulkImportReturn {
  file: File | null;
  rawRows: Record<string, string>[];
  mappedRows: BatchImportRow[];
  columnMapping: Record<string, string>;
  availableColumns: string[];
  loading: boolean;
  progress: number;
  jobId: string | null;
  jobStatus: string;
  parseFile: (file: File) => void;
  setColumnMapping: (mapping: Record<string, string>) => void;
  validateRows: () => BatchImportRow[];
  submitBatch: () => Promise<string | null>;
  reset: () => void;
}

export function useBulkImport(): UseBulkImportReturn {
  const [file, setFile] = useState<File | null>(null);
  const [rawRows, setRawRows] = useState<Record<string, string>[]>([]);
  const [mappedRows, setMappedRows] = useState<BatchImportRow[]>([]);
  const [columnMapping, setColumnMappingState] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [jobId, setJobId] = useState<string | null>(null);
  const [jobStatus, setJobStatus] = useState('');

  const availableColumns = rawRows.length > 0 ? Object.keys(rawRows[0]) : [];

  const parseFile = useCallback((csvFile: File) => {
    setFile(csvFile);
    setLoading(true);

    Papa.parse<Record<string, string>>(csvFile, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        setRawRows(results.data);
        const autoMapping: Record<string, string> = {};
        for (const col of REQUIRED_COLUMNS) {
          const match = availableColumns.find(
            (c) => c.toLowerCase().includes(col) || col.includes(c.toLowerCase()),
          );
          if (match) autoMapping[col] = match;
        }
        setColumnMappingState(autoMapping);
        setLoading(false);
      },
      error: () => {
        setLoading(false);
      },
    });
  }, []);

  const setColumnMapping = useCallback((mapping: Record<string, string>) => {
    setColumnMappingState(mapping);
  }, []);

  const validateRows = useCallback((): BatchImportRow[] => {
    const validated: BatchImportRow[] = rawRows.map((row, index) => {
      const mapped: Record<string, string> = {};
      const errors: string[] = [];

      for (const [required, source] of Object.entries(columnMapping)) {
        mapped[required] = row[source] || '';
      }

      if (!mapped.name) errors.push('Name is required');
      if (!mapped.price || isNaN(Number(mapped.price))) errors.push('Valid price is required');
      if (!mapped.category) errors.push('Category is required');
      if (!mapped.sku) errors.push('SKU is required');
      if (!mapped.stock || isNaN(Number(mapped.stock))) errors.push('Valid stock is required');
      if (!mapped.description) errors.push('Description is required');
      if (Number(mapped.price) < 0) errors.push('Price cannot be negative');
      if (Number(mapped.stock) < 0) errors.push('Stock cannot be negative');

      return {
        rowIndex: index,
        data: mapped,
        status: errors.length === 0 ? 'valid' : 'invalid',
        errors,
      };
    });

    setMappedRows(validated);
    return validated;
  }, [rawRows, columnMapping]);

  const submitBatch = useCallback(async (): Promise<string | null> => {
    const validRows = mappedRows.filter((r) => r.status === 'valid');
    if (validRows.length === 0) return null;

    setLoading(true);
    setProgress(0);

    try {
      const rowsData = validRows.map((r) => ({
        name: r.data.name,
        price: String(Number(r.data.price)),
        category: r.data.category,
        sku: r.data.sku,
        stock: String(Number(r.data.stock)),
        description: r.data.description,
        brand: r.data.brand || '',
        specs: r.data.specs || '',
      }));

      const result = await api.batch.submit(rowsData);
      setJobId(result.jobId);
      setJobStatus('processing');

      const pollInterval = setInterval(async () => {
        const status = await api.batch.status(result.jobId);
        setProgress(status.progress);
        setJobStatus(status.status);

        if (status.status === 'completed' || status.status === 'failed') {
          clearInterval(pollInterval);
          setLoading(false);
        }
      }, 2000);

      return result.jobId;
    } catch {
      setLoading(false);
      return null;
    }
  }, [mappedRows]);

  const reset = useCallback(() => {
    setFile(null);
    setRawRows([]);
    setMappedRows([]);
    setColumnMappingState({});
    setLoading(false);
    setProgress(0);
    setJobId(null);
    setJobStatus('');
  }, []);

  return {
    file,
    rawRows,
    mappedRows,
    columnMapping,
    availableColumns,
    loading,
    progress,
    jobId,
    jobStatus,
    parseFile,
    setColumnMapping,
    validateRows,
    submitBatch,
    reset,
  };
}
